import type { ReactNode } from "react";
import type { RaRecord } from "react-admin";
import { useRedirect } from "react-admin";
import { Box, Button as MuiButton, Chip, Typography } from "@mui/material";

import { formatDisplayDateTime } from "@/utils/date-format";

import PhoneLink from "@/components/PhoneLink";
import RelayItsSourceChip from "./RelayItsSourceChip";
import { SendRelayApprovalInviteAction } from "./SendRelayApprovalInviteButton";
import {
  getAccessRequestDisplayName,
  getAccessRequestItsSource,
  getAccessRequestMobile,
} from "./utils";

function formatAccessRequestEvent(record: RaRecord) {
  const event = record.relayEvent as { name?: string } | undefined;
  return event?.name?.trim() || null;
}

function MobileDetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "5.5rem minmax(0, 1fr)",
        columnGap: 1,
        alignItems: "start",
        py: 0.375,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, lineHeight: 1.5, pt: 0.125 }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, wordBreak: "break-word" }}>{children}</Box>
    </Box>
  );
}

function RelayAccessRequestMobileEditButton({ record }: { record: RaRecord }) {
  const redirect = useRedirect();

  return (
    <MuiButton
      variant="outlined"
      size="small"
      fullWidth
      onClick={(event) => {
        event.stopPropagation();
        redirect("edit", "relay-access-requests", record.id);
      }}
      sx={{ minHeight: 40, textTransform: "none" }}
    >
      Edit
    </MuiButton>
  );
}

function RelayAccessRequestMobileActions({
  record,
  canEdit,
}: {
  record: RaRecord;
  canEdit: boolean;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: canEdit ? "1fr 1fr" : "1fr",
        gap: 1,
        mt: 1.5,
        pt: 1.5,
        borderTop: 1,
        borderColor: "divider",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <SendRelayApprovalInviteAction record={record} variant="list" />
      {canEdit ? <RelayAccessRequestMobileEditButton record={record} /> : null}
    </Box>
  );
}

type RelayAccessRequestMobileListRowProps = {
  record: RaRecord;
  canEdit: boolean;
};

export function RelayAccessRequestMobileListPrimary({ record }: { record: RaRecord }) {
  const name = getAccessRequestDisplayName(record);
  const mobile = getAccessRequestMobile(record);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          mb: 0.75,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {String(record.itsId ?? "—")}
        </Typography>
        <RelayItsSourceChip source={getAccessRequestItsSource(record)} />
        {record.audioOnly ? (
          <Chip label="Audio only" size="small" variant="outlined" sx={{ height: 24 }} />
        ) : null}
      </Box>

      <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: "break-word", mb: 0.5 }}>
        {name}
      </Typography>

      <Typography variant="body2" color="text.secondary" component="div">
        <Box component="span" sx={{ fontWeight: 600, color: "text.secondary", mr: 0.5 }}>
          Mobile:
        </Box>
        <PhoneLink phone={mobile} stopPropagation />
      </Typography>
    </Box>
  );
}

export function RelayAccessRequestMobileListSecondary({
  record,
  canEdit,
}: RelayAccessRequestMobileListRowProps) {
  const eventName = formatAccessRequestEvent(record);
  const reason = typeof record.reason === "string" ? record.reason.trim() : "";
  const remarks = typeof record.remarks === "string" ? record.remarks.trim() : "";
  const granted = formatDisplayDateTime(record.createdAt, { empty: "" });

  return (
    <Box sx={{ minWidth: 0, mt: 0.5 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          py: 1,
          px: 1.25,
          borderRadius: 1,
          bgcolor: "action.hover",
        }}
      >
        {eventName ? (
          <MobileDetailRow label="Event">
            <Typography variant="body2">{eventName}</Typography>
          </MobileDetailRow>
        ) : null}
        {granted ? (
          <MobileDetailRow label="Granted">
            <Typography variant="body2">{granted}</Typography>
          </MobileDetailRow>
        ) : null}
        {reason ? (
          <MobileDetailRow label="Reason">
            <Typography variant="body2" color="text.primary">
              {reason}
            </Typography>
          </MobileDetailRow>
        ) : null}
        {remarks ? (
          <MobileDetailRow label="Remarks">
            <Typography variant="body2" color="text.secondary">
              {remarks}
            </Typography>
          </MobileDetailRow>
        ) : null}
        {!eventName && !granted && !reason && !remarks ? (
          <Typography variant="body2" color="text.secondary">
            No event details recorded
          </Typography>
        ) : null}
      </Box>

      <RelayAccessRequestMobileActions record={record} canEdit={canEdit} />
    </Box>
  );
}
